import React, { useRef, useEffect, useState } from 'react';
import { FaFileAlt, FaBalanceScale, FaProjectDiagram, FaHandshake, FaRulerCombined, FaWarehouse, FaShareAlt } from 'react-icons/fa';
import FocusAreaScrollIndicator from './FocusAreaScrollIndicator';

const WhatWeOffer = () => {
    const cardRefs = useRef([]);
    const [expandedIndex, setExpandedIndex] = useState(0); // Start with first card expanded
    const previousVisibleCards = useRef(new Set());
    const lastExpandedCardRef = useRef(0); // Track first card as initially expanded
    const manuallyCollapsedCards = useRef(new Set()); // Track cards manually collapsed by user
    const [hiddenKeyFocusAreas, setHiddenKeyFocusAreas] = useState({}); // Track which cards have hidden Key Focus Areas

    const services = [
        { 
            icon: <FaFileAlt />, 
            title: "Data Entry & Database Management", 
            desc: "At theadox.com, we ensure accurate, secure, and compliant management of your critical business data. We handle database organisation, data entry, preventive and corrective maintenance with precision, while providing full legal and compliance assurance. Our services streamline information workflows, safeguard sensitive records, and ensure regulatory adherence, allowing you to focus on strategic decision-making, business growth and personal wealth management." 
        },
        { 
            icon: <FaBalanceScale />, 
            title: "Legal & Compliance Support", 
            desc: "Ensure your every step directly or indirectly operates with full legal and regulatory confidence. We provide expert oversight of contracts, compliance processes, and legal documentation, safeguarding your operations and preparing you for audits. Our services help mitigate risk, streamline compliance workflows, and maintain adherence to all applicable laws and insurance requirements, including CAR (Contractors' All Risks), PI (Professional Indemnity), and HNW (High Net Worth) policies." 
        },
        { 
            icon: <FaProjectDiagram />, 
            title: "Project Management Support", 
            desc: "Ensure your projects are delivered on time, within budget, and to the highest standards with our comprehensive project management support. We provide end-to-end assistance across all phases—from planning and procurement to execution and reporting—optimising resources, mitigating risks, and maintaining clear communication with stakeholders. Our services also cover regulatory compliance, including CIP, DPU, DAP and shipping/logistics standards, with thorough checks to ensure full adherence." 
        },
        { 
            icon: <FaHandshake />, 
            title: "Communications and Relations", 
            desc: "At theadox.com, we recognise that effective communication and stakeholder engagement are essential for project success. By defining clear roles, implementing structured communication frameworks, and actively managing relationships, organisations can build trust, align expectations, and maintain accountability. Tailored messaging, two-way feedback, and commercial alignment further strengthen connections with clients, teams, and suppliers, fostering long-term, productive partnerships." 
        },
        { 
            icon: <FaRulerCombined />, 
            title: "Quantity Surveying & Estimating", 
            desc: "Optimise project finances with expert quantity surveying and estimating services. We deliver accurate cost assessments, detailed estimates, and effective budget management to maximise financial efficiency and returns. Our approach aligns with client briefs and project specifications, ensuring resources are allocated effectively, costs are controlled, and financial risks are mitigated." 
        },
        { 
            icon: <FaWarehouse />, 
            title: "Procurement & Vendor Management", 
            desc: "Enhance your supply chain with our strategic procurement and vendor management services. We implement tailored, intelligent processing workflows to optimise both direct and indirect supply, monitor costs, and map vendor performance. By building strong supplier relationships and overseeing inventory, we ensure a flexible, reliable supply chain that delivers consistent quality and operational efficiency." 
        },
        { 
            icon: <FaShareAlt />, 
            title: "Social Media & Content Management", 
            desc: "Enhance your brand's digital presence with structured, strategic social media and content management. By leveraging theadox.com's proven project framework, projects such as campaign launches, website redesigns, or content strategy implementations are delivered with clarity, accountability, and measurable outcomes. Our approach ensures social media activities align with business objectives, optimises engagement, and drives growth." 
        },
    ];

    const serviceDetails = {
        "Data Entry & Database Management": [
            "Budget and Timeline Tracking: Monitor project and operational budgets meticulously, ensuring strict adherence to financial plans and deadlines.",
            "Task Coordination: Manage appointments, meetings, and deadlines efficiently, coordinating calendars across clients and teams.",
            "Email Management: Organise, prioritise, and respond to emails promptly, flagging urgent matters and maintaining a structured inbox.",
            "Document Management: Systematically organise, store, and retrieve files; prepare reports, presentations, and other documentation; manage workflows efficiently.",
            "Data Entry & Database Management: Accurately capture and update within theadox.com management systems all business data pertaining to but not limited to customer records, warranty, and insurance information. ensuring compliance with local government regulations and third-party requirements.",
            "Warranty & Insurance Tracking: Monitor warranty periods, renewal dates, and insurance policies to ensure compliance and timely renewals with the option to facilitate the renewal process to obtain quotations when required.",
            "Value Engineering & Performance Assurance: Analyse processes and introduce critical metrics required to enhance clarity, strengthen communication, and guide smarter, more confident decisions."
        ],
        "Legal & Compliance Support": [
            "Legal Research: Investigate relevant laws, regulations, and policies affecting contracts, operations, or compliance.",
            "Contract Drafting & Review: Draft and review legal documents to ensure clarity, mitigate risk, and protect your business interests.",
            "Compliance Monitoring & Support: Ensure business activities comply with regulatory standards, maintain compliance records, and prepare thoroughly for audits, including CAR, PI, and HNW insurance obligations.",
            "Legal Documentation Management: Organise and securely store legal documents, tracking renewal dates, ongoing legal obligations, and safe guarding all digital receipts and but not limited to signed NDAs"
        ],
        "Project Management Support": [
            "Project Planning & Initiation: Establish the project foundation by defining objectives, scope, risks, costs, and quality requirements. Develop detailed schedules, assign tasks, and plan resources to ensure the project starts with a clear and actionable roadmap.",
            "Procurement & Contracts: Evaluate suppliers, costs, and logistics to select the most effective procurement options. Draft, review, and manage contracts, ensuring compliance with CIP, DPU, DAP, and all relevant shipping/logistics regulations.",
            "Stage Management: Oversee each stage of the project by monitoring progress, managing work packages, allocating resources efficiently, and addressing issues promptly to keep the project on track.",
            "Product Delivery: Coordinate with teams and team managers to ensure products or deliverables are developed according to specifications, meet quality standards, and gain timely stakeholder approval.",
            "Reporting & Communication: Maintain transparent communication by providing regular updates, dashboards, and progress reports to clients and stakeholders, ensuring all parties are informed and aligned throughout the project lifecycle.",
            "Project Oversight & Closure: Guide the project from start to finish, including stage reviews and approvals, final delivery, evaluation of performance, capturing lessons learned, and releasing resources, ensuring a controlled and successful project closure."
        ],
        "Communications and Relations": [
            "Due Stakeholder Engagement: Ensure project success by actively managing relationships with all stakeholders, keeping them informed and involved. Use structured communication to build trust, foster shared understanding, and align expectations across the project ecosystem.",
            "Defined Roles & Responsibility Mapping: Establish clear roles and responsibilities following theadox.com principles of Business, User, Supplier. This ensures that communication and decision-making are appropriately distributed, all perspectives are considered, and accountability is maintained.",
            "Communication Management Approach: Develop a comprehensive communication strategy that outlines procedures, tools, frequency, and responsibilities for information exchange. This structured framework ensures messages are delivered effectively and consistently across the project.",
            "Controlled & Bidirectional Flow: Maintain a structured flow of information to prevent misunderstandings, while also emphasising listening and gathering feedback from stakeholders. This ensures concerns are addressed proactively and fosters an open, responsive communication environment.",
            "Tailored Messaging: Adapt communication to specific audiences, both internal and external, considering their interests, concerns, and influence. Customised messaging enhances engagement, prevents issues, and strengthens stakeholder relationships.",
            "Commercial Alignment: Manage relationships in commercial contexts by aligning clients and customers needs with supplier delivery. Use clear communication, project governance frameworks, and business cases to ensure expectations are met and all parties are aligned.",
            "Client Lifecycle Management: Provide end-to-end communications and relations support, from client onboarding to ongoing engagement. Maintain seamless interactions, high satisfaction levels, and structured information flow using frameworks such as RACI, master matrix schedules, and linear responsibility charts.",
            "Due Diligence & Risk Management: Conduct thorough assessments—including financial checks, risk evaluations, and vendor analysis—to inform communications and decisions, mitigating potential issues before they arise.",
            "Customer Support & Ongoing Communication: Respond promptly to client inquiries, resolve issues efficiently, and provide regular updates on project status, services, or changes. Structured communication matrices ensure consistent engagement and reinforce trust throughout the client lifecycle."
        ],
        "Quantity Surveying & Estimating": [
            "Take-Offs & Measurements: Quantify materials, labour, and equipment requirements based on project specifications to provide a comprehensive foundation for accurate planning and cost estimation.",
            "Cost Estimates & Budgeting: Prepare detailed cost estimates that reflect client requirements, project specifications, and desired outcomes, incorporating materials, labour, overheads, and contingencies to ensure financial viability.",
            "Billing & Documentation: Generate progress claims, interim bills, and final accounts, and prepare essential documentation such as Bills of Quantities (BOQs), tender reports, and contractual financial records.",
            "Tender & Bid Preparation: Support the preparation of tender documents, proposals, and bids to secure projects effectively, ensuring all cost and specification details are accurately represented.",
            "Cost Control & Variance Analysis: Monitor project expenses against budgets, identify variances, and implement corrective actions to maintain financial efficiency throughout the project lifecycle.",
            "Value Engineering: Analyse design and execution options to optimise costs while maintaining quality, performance, and alignment with client objectives.",
            "Financial Risk Assessment: Identify potential financial risks, evaluate their impact, and develop mitigation strategies to safeguard the project's financial stability."
        ],
        "Procurement & Vendor Management": [
            "Vendor & Supplier Relations: Build and maintain effective relationships, negotiating favourable terms, pricing, and service agreements.",
            "Order & Purchase Management: Process purchase orders, track deliveries, and manage invoicing to ensure timely and accurate procurement.",
            "Inventory & Stock Management: Monitor stock levels, manage reorder points, and coordinate procurement to prevent shortages or overstocking.",
            "Vendor Performance Evaluation: Assess vendor reliability, quality, and compliance, while managing contracts, performance metrics, cost mapping, and overall supply chain performance."
        ],
        "Social Media & Content Management": [
            "Content Creation & Curation: Develop original posts, articles, graphics, and multimedia content that reflect your brand voice, values, and strategic objectives, delivering tangible products such as content calendars, blog sections, or campaign assets.",
            "Social Media Scheduling & Publishing: Plan, schedule, and manage posts across platforms using specialised tools to optimise reach, engagement, and campaign effectiveness.",
            "Brand Strategy & Development: Define brand voice, visual identity, and social media campaigns, ensuring consistency in messaging and reinforcing audience recognition and engagement.",
            "Analytics & Performance Tracking: Monitor metrics and campaign results to evaluate performance, inform strategic adjustments, and provide actionable insights for continuous improvement.",
            "Project Governance & Framework: Apply theadox.com's structured methodology to ensure continued business justification, clearly defined roles, stage-based management, and exception-based decision-making. Document lessons learned throughout campaigns to refine future social media strategies and maintain efficiency.",
            "Legal Protection & Rights Management: Provide comprehensive guidance on intellectual property, publicity, and privacy laws; assist with image rights registration; draft and manage licensing and endorsement contracts; and monitor and enforce protections to safeguard the client's persona.",
            "Licensing & Commercial Exploitation: Strategically negotiate and secure licensing deals for the client's image across advertising, campaigns, and merchandise; define precise terms including scope, duration, territory, and compensation; and collaborate with brands, media, and content creators to maximise commercial value."
        ]
    };

    // Intersection Observer for individual cards - fade in/out only at page borders
    useEffect(() => {
        const updateCardVisibilities = () => {
            const newVisibilities = {};
            const currentVisibleCards = new Set();
            const viewportHeight = window.innerHeight;
                const fadeZone = 400; // Distance from border where fade starts (in pixels) - increased to keep cards visible longer
            
            cardRefs.current.forEach((cardRef, index) => {
                if (!cardRef) return;
                
                const rect = cardRef.getBoundingClientRect();
                const top = rect.top;
                const bottom = rect.bottom;
                
                // Card is in viewport (with extended buffer zone to keep cards visible longer)
                const bufferZone = 100; // Additional buffer to keep cards visible
                const isInViewport = top < viewportHeight + bufferZone && bottom > -bufferZone;
                // Current card is fully visible in viewport (with buffer)
                const isFullyVisible = top >= -bufferZone && bottom <= viewportHeight + bufferZone;
                
                // Track visible cards
                if (isInViewport) {
                    currentVisibleCards.add(index);
                }
                
                // Calculate opacity: fade out only when reaching page borders
                // Keep expanded cards and recently collapsed cards fully visible even when partially out of viewport
                let opacity;
                const isExpanded = expandedIndex === index;
                const wasRecentlyExpanded = lastExpandedCardRef.current === index;
                
                // More lenient fade logic - cards stay visible longer
                if (!isInViewport && !isExpanded && !wasRecentlyExpanded) {
                    // Card completely outside viewport (with buffer) and not expanded/recently expanded - fade out gradually
                    const distanceFromViewport = top > viewportHeight 
                        ? top - viewportHeight 
                        : -bottom;
                    const fadeDistance = fadeZone * 2; // Even longer fade distance
                    opacity = Math.max(0, 1 - (distanceFromViewport / fadeDistance));
                } else if (isFullyVisible || isExpanded || wasRecentlyExpanded) {
                    // Card fully visible, expanded, or recently expanded - stay at full opacity
                    opacity = 1;
                } else {
                    // Card partially visible - fade based on distance from borders (more gradual)
                    let fadeFromTop = 1;
                    let fadeFromBottom = 1;
                    
                    // Fade when approaching top border (more gradual transition)
                    if (top < fadeZone) {
                        fadeFromTop = Math.max(0.3, top / fadeZone); // Minimum 0.3 opacity for visibility
                    }
                    
                    // Fade when approaching bottom border (more gradual transition)
                    if (bottom > viewportHeight - fadeZone) {
                        fadeFromBottom = Math.max(0.3, (viewportHeight - bottom) / fadeZone); // Minimum 0.3 opacity for visibility
                    }
                    
                    // Use the minimum (most faded) value, but keep a minimum opacity
                    opacity = Math.max(0.3, Math.min(fadeFromTop, fadeFromBottom));
                }
                
                newVisibilities[index] = {
                    isVisible: isInViewport,
                    opacity: opacity,
                    translateY: isFullyVisible ? 0 : (top < 0 ? -40 : 30),
                    scale: isFullyVisible ? 1 : (top < 0 ? 0.9 : 0.95),
                    zIndex: isFullyVisible ? (10 + index) : (top < 0 ? 20 + index : index)
                };
            });
            
            // Auto-expand cards when they enter viewport
            // Find cards that just entered viewport
            const newlyEnteredCards = Array.from(currentVisibleCards).filter(
                cardIndex => !previousVisibleCards.current.has(cardIndex)
            );
            
            // Detect cards that left viewport and reset them (refresh behavior)
            const cardsThatLeft = Array.from(previousVisibleCards.current).filter(
                cardIndex => !currentVisibleCards.has(cardIndex)
            );
            
            // Reset cards that left viewport - clear expanded state and manual collapse
            cardsThatLeft.forEach(cardIndex => {
                if (expandedIndex === cardIndex) {
                    // If the expanded card left viewport, collapse it
                    setExpandedIndex(null);
                }
                manuallyCollapsedCards.current.delete(cardIndex); // Clear manual collapse when card leaves
            });
            
            // Auto-expand logic: expand the first card that enters viewport or is already visible
            // IMPORTANT: Only expand cards that are NOT above the top of viewport
            // Skip this section - the auto-expand logic is handled after the collapse check below
            
            // Auto-collapse expanded card ONLY when:
            // 1. A new card has significantly entered the viewport (scrolling down)
            // 2. AND the expanded card is moving out of view (above or near top)
            // This prevents aggressive auto-collapse when new cards just peek in
            // FIXED: Only check if current expanded card is manually collapsed, not all cards
            if (expandedIndex !== null && !manuallyCollapsedCards.current.has(expandedIndex)) {
                const expandedCardRef = cardRefs.current[expandedIndex];
                if (expandedCardRef) {
                    const expandedRect = expandedCardRef.getBoundingClientRect();
                    const expandedTop = expandedRect.top;
                
                    // Only auto-collapse if:
                    // 1. Expanded card is significantly above the viewport (at least 100px above)
                    // 2. OR a next card has entered the viewport AND is significantly visible (at least 30% visible)
                    const isExpandedCardAboveViewport = expandedTop < -100; // Card is 100px+ above viewport
                    
                    // Check if a next card has entered and is significantly visible
                    const hasNextCardSignificantlyVisible = Array.from(currentVisibleCards).some(cardIndex => {
                        if (cardIndex <= expandedIndex) return false; // Not a next card
                        if (!previousVisibleCards.current.has(cardIndex)) {
                            // This is a newly entered card
                            const cardRef = cardRefs.current[cardIndex];
                            if (!cardRef) return false;
                            const cardRect = cardRef.getBoundingClientRect();
                            const cardHeight = cardRect.height;
                            const visibleHeight = Math.min(cardRect.bottom, viewportHeight) - Math.max(cardRect.top, 0);
                            const visibilityPercentage = cardHeight > 0 ? (visibleHeight / cardHeight) : 0;
                            
                            // Card must be at least 30% visible to trigger collapse
                            return visibilityPercentage >= 0.3 && cardRect.top < viewportHeight - 200;
                        }
                        return false;
                    });
                    
                    // Only collapse if expanded card is well above viewport OR next card is significantly visible
                    if (isExpandedCardAboveViewport || (hasNextCardSignificantlyVisible && expandedTop < 50)) {
                        // Auto-collapse the expanded card
                        setExpandedIndex(null);
                        lastExpandedCardRef.current = expandedIndex; // Keep track of which card was collapsed
                    }
                }
            }
            
            // Auto-expand card when it gets close to navbar (near top of viewport)
            // This happens when scrolling - card near navbar should auto-expand
            // Priority: Cards close to navbar (150-250px from top) should auto-expand
            const navbarProximityZone = { min: 150, max: 250 }; // Distance from top where cards should auto-expand
            
            // Check for cards near navbar (regardless of current expanded state, but respect manual collapse per card)
            // FIXED: Only block auto-expand for specific manually collapsed cards, not all cards
            let cardNearNavbar = null;
            let closestDistanceToNavbar = Infinity;
            
            // Find the card closest to the navbar (within proximity zone)
            // Only skip cards that are manually collapsed, but allow others to expand
            Array.from(currentVisibleCards).forEach(cardIndex => {
                // Skip only this specific card if it's manually collapsed
                if (manuallyCollapsedCards.current.has(cardIndex)) return;
                
                const cardRef = cardRefs.current[cardIndex];
                if (!cardRef) return;
                
                const cardRect = cardRef.getBoundingClientRect();
                const cardTop = cardRect.top;
                const cardBottom = cardRect.bottom;
                
                // Card must be visible and in viewport
                if (cardTop < viewportHeight && cardBottom > 0) {
                    // Check if card is in navbar proximity zone (150-250px from top)
                    if (cardTop >= navbarProximityZone.min && cardTop <= navbarProximityZone.max) {
                        const distanceToNavbar = Math.abs(cardTop - (navbarProximityZone.min + navbarProximityZone.max) / 2);
                        
                        // Track the card closest to the center of proximity zone
                        if (distanceToNavbar < closestDistanceToNavbar) {
                            closestDistanceToNavbar = distanceToNavbar;
                            cardNearNavbar = cardIndex;
                    }
                }
                }
            });
                
            // If we found a card near navbar, expand it (even if another card is already expanded)
            // Only expand if this specific card is not manually collapsed
            if (cardNearNavbar !== null && cardNearNavbar !== expandedIndex && !manuallyCollapsedCards.current.has(cardNearNavbar)) {
                setExpandedIndex(cardNearNavbar);
                lastExpandedCardRef.current = cardNearNavbar;
            }
            
            // Auto-expand first visible card that is NOT above the top of viewport
            // This happens after expanded card reaches top and collapses, or when scrolling and no card is expanded
            // Also handles initial load and newly entered cards
            // FIXED: Only block auto-expand for specific manually collapsed cards, not all cards
            if (expandedIndex === null) {
                    // Find the first visible card that is at or below the top of viewport
                    let cardToExpand = null;
                    const topThreshold = 100; // Cards must be at least 100px from top (not above viewport)
                    
                    // Check newly entered cards first (cards that just entered viewport)
                    if (newlyEnteredCards.length > 0) {
                        // Clear manual collapse for cards that re-entered (refresh behavior)
                        // Only clear if enough time has passed (card scrolled away and came back)
                        newlyEnteredCards.forEach(cardIndex => {
                            // Only clear if card was scrolled away and is coming back (not just entered)
                            const cardRef = cardRefs.current[cardIndex];
                            if (cardRef) {
                                const cardRect = cardRef.getBoundingClientRect();
                                // If card is well below the top (scrolled back into view), clear manual collapse
                                if (cardRect.top > 200) {
                                    manuallyCollapsedCards.current.delete(cardIndex);
                }
            }
                        });
                        
                        // Filter newly entered cards that are not above the top and not manually collapsed
                        // FIXED: Check manual collapse per card, not globally
                        const validNewCards = newlyEnteredCards
                            .filter(cardIndex => {
                                // Only skip this specific card if it's manually collapsed
                                if (manuallyCollapsedCards.current.has(cardIndex)) return false;
                                const cardRef = cardRefs.current[cardIndex];
                                if (!cardRef) return false;
                                const cardRect = cardRef.getBoundingClientRect();
                                // Card should be visible and not completely above the top of viewport
                                return cardRect.bottom > topThreshold && cardRect.top <= viewportHeight + 100;
                            })
                            .sort((a, b) => a - b);
                        
                        if (validNewCards.length > 0) {
                            cardToExpand = validNewCards[0];
                        }
                    }
                    
                    // If no newly entered card found, check all visible cards
                    // FIXED: Only skip manually collapsed cards, allow others to expand
                    if (cardToExpand === null) {
                        const visibleCardsArray = Array.from(currentVisibleCards)
                            .filter(cardIndex => {
                                // Only skip this specific card if it's manually collapsed
                                if (manuallyCollapsedCards.current.has(cardIndex)) return false;
                                
                                const cardRef = cardRefs.current[cardIndex];
                                if (!cardRef) return false;
                                
                                const cardRect = cardRef.getBoundingClientRect();
                                // Card should be visible and not completely above the top of viewport
                                return cardRect.bottom > 0 && cardRect.top <= viewportHeight + 100;
                            })
                            .sort((a, b) => a - b);
                        
                        // Find the first card that is below or at the top threshold (not above viewport)
                        for (let cardIndex of visibleCardsArray) {
                            const cardRef = cardRefs.current[cardIndex];
                            if (!cardRef) continue;
                            
                            const cardRect = cardRef.getBoundingClientRect();
                            // Prefer cards that are below the top threshold (not above viewport)
                            if (cardRect.top >= topThreshold || (cardRect.top < topThreshold && cardRect.bottom > topThreshold)) {
                                cardToExpand = cardIndex;
                                break;
                }
            }
            
                        // If no card found below threshold, use the first visible card that's not above viewport
                        if (cardToExpand === null && visibleCardsArray.length > 0) {
                            // Find first card that has bottom edge visible
                            for (let cardIndex of visibleCardsArray) {
                                const cardRef = cardRefs.current[cardIndex];
                                if (!cardRef) continue;
                                const cardRect = cardRef.getBoundingClientRect();
                                if (cardRect.bottom > 0) {
                                    cardToExpand = cardIndex;
                                    break;
                                }
                            }
                        }
                    }
                    
                    // Only expand if we found a valid card that is NOT manually collapsed
                    // FIXED: Check per card, not globally blocked
                    if (cardToExpand !== null && cardToExpand !== undefined && !manuallyCollapsedCards.current.has(cardToExpand)) {
                        setExpandedIndex(cardToExpand);
                        lastExpandedCardRef.current = cardToExpand;
                }
            }
            // Update previous visible cards for next check
            previousVisibleCards.current = currentVisibleCards;
            
            // Card visibilities calculated but not used for now
        };
        
        // Initial update
        updateCardVisibilities();
        
        // On mobile, throttle scroll to reduce lag (fewer RAF calls)
        const isMobile = window.innerWidth < 1368;
        let scrollThrottleRef = null;
        const throttleMs = 120;
        const handleScroll = () => {
            if (isMobile) {
                if (scrollThrottleRef !== null) return;
                scrollThrottleRef = window.setTimeout(() => {
                    scrollThrottleRef = null;
                    requestAnimationFrame(updateCardVisibilities);
                }, throttleMs);
            } else {
                requestAnimationFrame(updateCardVisibilities);
            }
        };
        
        window.addEventListener('scroll', handleScroll, { passive: true });
        window.addEventListener('resize', handleScroll, { passive: true });
        
        return () => {
            if (scrollThrottleRef) clearTimeout(scrollThrottleRef);
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleScroll);
        };
    }, [services.length, expandedIndex]);

    // Separate IntersectionObserver for auto-expanding cards when they first become visible
    useEffect(() => {
        if (expandedIndex !== null) return; // Don't set up observers if a card is already expanded
        // FIXED: Removed global blocking - only block specific manually collapsed cards
        
        const observers = [];
        let firstVisibleCardIndex = null;
        
        cardRefs.current.forEach((cardRef, index) => {
            if (!cardRef) return;
            
            const observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            // Don't auto-expand if this card is manually collapsed
                            if (manuallyCollapsedCards.current.has(index)) {
                                return;
                            }
                            
                            // Clear manual collapse when card becomes visible again (on scroll) - only if it's a different card
                            if (!manuallyCollapsedCards.current.has(index)) {
                            manuallyCollapsedCards.current.delete(index);
                            }
                            
                            // Track the first visible card
                            if (firstVisibleCardIndex === null || index < firstVisibleCardIndex) {
                                firstVisibleCardIndex = index;
                            }
                            
                            // Auto-expand the first visible card (only if not manually collapsed)
                            if (expandedIndex === null && firstVisibleCardIndex === index && !manuallyCollapsedCards.current.has(index)) {
                                setExpandedIndex(index);
                                lastExpandedCardRef.current = index;
                            }
                        }
                    });
                },
                { 
                    threshold: 0.2, // Trigger when 20% of card is visible
                    rootMargin: '0px'
                }
            );
            
            observer.observe(cardRef);
            observers.push(observer);
        });
        
        return () => {
            observers.forEach(observer => observer.disconnect());
        };
    }, [services.length, expandedIndex]);

    return (
        <>
            {/* Heading Section - Separate container with scroll snap */}
            <section 
                className="snap-section flex items-center justify-center bg-transparent"
                style={{ 
                    scrollSnapAlign: 'center',
                    minHeight: '100vh',
                    paddingTop: 'clamp(80px, 15vh, 140px)',
                    paddingBottom: 'clamp(40px, 8vh, 80px)'
                }}
            >
                <div className="w-full mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-[1in]">
                    <div className="text-left">
                        <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-[#E68A40] leading-tight mb-4 sm:mb-6 md:mb-8">
                            Empowering Businesses Through Smarter Support Solutions
                        </h2>
                        <div className="text-white text-base sm:text-lg md:text-xl lg:text-2xl leading-relaxed sm:leading-[1.8rem] md:leading-[2.0rem] lg:leading-[2.25rem] tracking-[0.02em] sm:tracking-[0.03em] md:tracking-[0.04em] text-justify">
                            <p>
                                At theadox.com, We are certain that every organisation and individual faces unique challenges, which is why we offer bespoke non-subscription solutions designed to meet your specific needs. By combining precise data management with strategic insight, we generate the critical metrics required to enhance clarity, strengthen communication, and guide smarter, more confident decisions—so you can work smarter, achieve more, and deliver sustainable, high-performing results. We take care of the complexity, doing the work for you.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Cards - Each in its own container with scroll snap */}
            {services.map((service, index) => {
                return (
                    <section 
                        key={index}
                        ref={(el) => {
                            cardRefs.current[index] = el;
                        }}
                        data-what-we-offer-card="true"
                        className="snap-section flex items-start sm:items-center justify-center bg-transparent px-3 sm:px-4 md:px-6 lg:px-8"
                        style={{ 
                            scrollSnapAlign: 'start',
                            minHeight: '100vh',
                            paddingTop: 'clamp(80px, 15vh, 140px)',
                            paddingBottom: 'clamp(40px, 8vh, 80px)'
                        }}
                    >
                        <div 
                            className="group relative w-full max-w-7xl mx-auto"
                            style={{
                                display: 'flex',
                                flexDirection: 'column'
                            }}
                        >
                            <div 
                                className="w-full overflow-visible flex flex-col backdrop-blur-sm transition-all duration-300"
                                style={{
                                    background: index % 2 === 0 
                                        ? 'linear-gradient(135deg, rgba(8, 14, 31, 0.95) 0%, rgba(10, 18, 37, 0.95) 50%, rgba(8, 14, 31, 0.95) 100%)' 
                                        : 'linear-gradient(135deg, rgba(10, 18, 37, 0.95) 0%, rgba(8, 14, 31, 0.95) 50%, rgba(10, 18, 37, 0.95) 100%)',
                                    border: '1px solid',
                                    borderImageSource: 'linear-gradient(113.96deg, rgba(255, 231, 231, 0.46) 0%, rgba(255, 255, 255, 0.47) 7.3%, rgba(0, 0, 0, 0) 23.96%, rgba(255, 255, 255, 0.3) 57.81%, rgba(115, 129, 255, 0.45) 81.28%, rgba(0, 7, 73, 0.31) 100%)',
                                    borderImageSlice: 1,
                                    borderRadius: '0',
                                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 1px rgba(230, 138, 64, 0.3) inset',
                                    padding: 'clamp(1rem, 3vw, 2rem)'
                                }}
                            >
                                <div className="flex-shrink-0 flex items-center py-2 sm:py-3 md:py-4 lg:py-6">
                                    <div className="w-full">
                                        {/* Text Content */}
                                        <div className="flex flex-col justify-center space-y-2 sm:space-y-3 md:space-y-4">
                                            <h3 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-white leading-tight">
                                                {service.title}
                                            </h3>
                                            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-300 leading-relaxed text-justify line-clamp-none sm:line-clamp-4">
                                                {service.desc}
                                            </p>
                                        </div>

                                        {/* Fold/Collapse Button */}
                                        {serviceDetails[service.title] && (
                                            <div className="mt-3 sm:mt-4">
                                                <button
                                                    onClick={() => {
                                                        setHiddenKeyFocusAreas(prev => ({
                                                            ...prev,
                                                            [index]: !prev[index]
                                                        }));
                                                    }}
                                                    className="w-full sm:w-auto px-4 sm:px-5 md:px-6 py-2.5 sm:py-3 bg-[#E68A40] text-white rounded-lg font-semibold text-sm sm:text-base hover:bg-[#cf7a31] transition-all duration-300 transform hover:scale-105 inline-flex items-center justify-center whitespace-nowrap min-h-[44px]"
                                                >
                                                    {hiddenKeyFocusAreas[index] ? 'Show Details' : 'Hide Details'}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Expandable Content - Toggleable - MOBILE RESPONSIVE FIX */}
                                <div 
                                    className="overflow-hidden transition-all duration-500 ease-in-out"
                                    style={{
                                        maxHeight: hiddenKeyFocusAreas[index] ? '0' : 'none',
                                        opacity: hiddenKeyFocusAreas[index] ? 0 : 1,
                                        paddingTop: hiddenKeyFocusAreas[index] ? '0' : 'clamp(0.5rem, 2vw, 1rem)',
                                        paddingBottom: hiddenKeyFocusAreas[index] ? '0' : 'clamp(0.25rem, 1vw, 0.5rem)'
                                    }}
                                >
                                    {serviceDetails[service.title] ? (
                                        <div 
                                            className="bg-gradient-to-br from-[#050b1b]/90 to-[#08101f]/90 border border-white/10 rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-5 lg:p-6 shadow-[0_8px_24px_rgba(0,0,0,0.4)] backdrop-blur-sm relative overflow-visible"
                                            style={{
                                                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4), 0 0 1px rgba(255, 255, 255, 0.1) inset'
                                            }}
                                        >
                                            <h4 className="text-base sm:text-lg md:text-xl font-semibold text-white mb-2 sm:mb-3">Key Focus Areas</h4>
                                            <div className="overflow-visible">
                                                <FocusAreaScrollIndicator items={serviceDetails[service.title]} />
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-gray-300 text-sm md:text-base text-justify">
                                            {service.desc}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </section>
                );
            })}
        </>
    );
};

export default WhatWeOffer;